import { Bot, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksPage } from '../../components/liks/LiksPage/LiksPage';
import { LiksRefreshButton } from '../../components/liks/LiksRefreshButton/LiksRefreshButton';
import { LiksSearch } from '../../components/liks/LiksSearch/LiksSearch';
import { useCreateChatbotMutation } from '../../features/chatbots/mutations/useCreateChatbotMutation';
import { useChatbotsQuery } from '../../features/chatbots/queries/useChatbotsQuery';
import { useChatbotsPageStore } from '../../features/chatbots/stores/useChatbotsPageStore';
import { useKnowledgeDatabasesEligibleForChatbotCreate } from '../../features/knowledge/hooks/useKnowledgeDatabasesEligibleForChatbotCreate';
import { useKnowledgeDatabasesQuery } from '../../features/knowledge/queries/useKnowledgeDatabasesQuery';
import { useI18n } from '../../i18n/useI18n';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import { ChatbotListCard } from './ChatbotListCard';
import {
  compareAccessibleChatbotsByStampDesc,
  resolveChatbotListPath,
} from './chatbotsPageHelpers';
import { CreateChatbotDialog } from './CreateChatbotDialog';
import './ChatbotsPage.css';

export function ChatbotsPage() {
  const { locale, translate } = useI18n();
  const navigate = useNavigate();
  const chatbotsQuery = useChatbotsQuery();
  const databasesQuery = useKnowledgeDatabasesQuery();
  const createChatbotMutation = useCreateChatbotMutation();
  const [searchQuery, setSearchQuery] = useState('');
  const isCreateDialogOpen = useChatbotsPageStore(
    (state) => state.isCreateDialogOpen,
  );
  const openCreateDialog = useChatbotsPageStore(
    (state) => state.openCreateDialog,
  );
  const closeCreateDialog = useChatbotsPageStore(
    (state) => state.closeCreateDialog,
  );
  const eligibleDatabasesQuery = useKnowledgeDatabasesEligibleForChatbotCreate({
    enabled: isCreateDialogOpen,
  });

  const accessibleChatbots = chatbotsQuery.data ?? [];
  const databases = useMemo(
    () => databasesQuery.data ?? [],
    [databasesQuery.data],
  );
  const knowledgeBaseNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const database of databases) {
      map.set(database.id, database.name);
    }
    return map;
  }, [databases]);

  const knowledgeBaseOptions = useMemo(
    () =>
      eligibleDatabasesQuery.databases
        .map((database) => ({
          value: database.id,
          label: database.name,
        }))
        .sort((left, right) =>
          left.label.localeCompare(right.label, locale, {
            sensitivity: 'base',
          }),
        ),
    [eligibleDatabasesQuery.databases, locale],
  );

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredChatbots = (
    normalizedSearch === ''
      ? accessibleChatbots
      : accessibleChatbots.filter(({ chatbot }) => {
          const name = chatbot.name?.toLowerCase() ?? '';
          const displayName = chatbot.displayName?.toLowerCase() ?? '';
          const knowledgeBaseName = (
            knowledgeBaseNameById.get(chatbot.knowledgeBaseId ?? '') ?? ''
          ).toLowerCase();
          return (
            name.includes(normalizedSearch) ||
            displayName.includes(normalizedSearch) ||
            knowledgeBaseName.includes(normalizedSearch)
          );
        })
  )
    .slice()
    .sort(compareAccessibleChatbotsByStampDesc);

  const isPageLoading = chatbotsQuery.isLoading;

  const handleManualRefresh = () => {
    void chatbotsQuery.refetch();
  };

  const handleCreate = ({
    name,
    knowledgeBaseId,
  }: {
    name: string;
    knowledgeBaseId: string;
  }) => {
    createChatbotMutation.mutate(
      { name, knowledgeBaseId },
      {
        onSuccess: (chatbot) => {
          toast.success({
            title: translate('chatbots.createSuccessTitle'),
            description: translate('chatbots.createSuccessDescription', {
              name,
            }),
          });
          closeCreateDialog();
          navigate(resolveChatbotListPath(chatbot.id));
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('chatbots.createErrorTitle'),
            translate('chatbots.createErrorDescription'),
          );
        },
      },
    );
  };

  return (
    <>
      <LiksPage
        header={
          <header className="chatbots-page__header">
            <h1>{translate('chatbots.title')}</h1>
            <div className="chatbots-page__toolbar">
              <LiksSearch
                className="chatbots-page__toolbar-search"
                variant="on-light"
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={translate('chatbots.searchPlaceholder')}
                clearLabel={translate('common.search.clear')}
                aria-label={translate('chatbots.searchLabel')}
              />
              <LiksButton
                className="chatbots-page__create-button"
                onClick={openCreateDialog}
              >
                <Plus strokeWidth={2} aria-hidden />
                {translate('chatbots.create')}
              </LiksButton>
              <LiksRefreshButton
                className="chatbots-page__refresh-button"
                onRefresh={handleManualRefresh}
              />
            </div>
          </header>
        }
        content={
          <div className="chatbots-page__content">
            {isPageLoading ? (
              <p className="chatbots-page__status">
                {translate('chatbots.loading')}
              </p>
            ) : chatbotsQuery.isError ? (
              <p className="chatbots-page__status" role="alert">
                {translate('chatbots.loadError')}
              </p>
            ) : accessibleChatbots.length === 0 ? (
              <div className="chatbots-page__empty">
                <Bot
                  className="chatbots-page__empty-icon"
                  strokeWidth={1.75}
                  aria-hidden
                />
                <p>{translate('chatbots.empty')}</p>
              </div>
            ) : filteredChatbots.length === 0 ? (
              <div className="chatbots-page__empty">
                <Bot
                  className="chatbots-page__empty-icon"
                  strokeWidth={1.75}
                  aria-hidden
                />
                <p>{translate('chatbots.noResults')}</p>
              </div>
            ) : (
              <ul className="chatbots-page__list">
                {filteredChatbots.map((entry) => (
                  <ChatbotListCard
                    key={entry.chatbot.id}
                    entry={entry}
                    knowledgeBaseName={
                      knowledgeBaseNameById.get(
                        entry.chatbot.knowledgeBaseId ?? '',
                      ) ?? translate('chatbots.unknownKnowledgeBase')
                    }
                  />
                ))}
              </ul>
            )}
          </div>
        }
      />

      {isCreateDialogOpen && (
        <CreateChatbotDialog
          title={translate('chatbots.createDialogTitle')}
          nameLabel={translate('chatbots.form.nameLabel')}
          namePlaceholder={translate('chatbots.form.namePlaceholder')}
          knowledgeBaseLabel={translate('chatbots.form.knowledgeBaseLabel')}
          knowledgeBasePlaceholder={translate(
            'chatbots.form.knowledgeBasePlaceholder',
          )}
          knowledgeBaseOptions={knowledgeBaseOptions}
          createLabel={translate('chatbots.form.create')}
          cancelLabel={translate('chatbots.form.cancel')}
          closeLabel={translate('chatbots.form.close')}
          isSubmitting={
            createChatbotMutation.isPending || eligibleDatabasesQuery.isLoading
          }
          onCreate={handleCreate}
          onClose={closeCreateDialog}
        />
      )}
    </>
  );
}
